import { FC, ReactNode, useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { HardDrive } from "lucide-react";
import { Modal } from "@monolith-forensics/monolith-ui";

import type { LabAssetType } from "../labAssets.types";
import { LAB_ASSET_TYPE_LABELS } from "../labAssets.utils";
import CreateLabAssetForm from "../Forms/CreateLabAssetForm";
import MonitorCloudIcon from "../components/MonitorCloudIcon";

interface AssetTypeOption {
  label: string;
  value: LabAssetType;
  description: string;
  icon: ReactNode;
}

// The UI says "Hardware" but the API's asset_type enum is
// "equipment" | "software" — sending "hardware" is a 400.
const ASSET_TYPE_OPTIONS: AssetTypeOption[] = [
  {
    label: LAB_ASSET_TYPE_LABELS.equipment,
    value: "equipment",
    description:
      "Write blockers, workstations, drives, and other lab equipment",
    icon: <HardDrive size={36} strokeWidth={1.5} />,
  },
  {
    label: LAB_ASSET_TYPE_LABELS.software,
    value: "software",
    description: "Forensic tools, licenses, and subscriptions",
    icon: <MonitorCloudIcon size={36} strokeWidth={1.5} />,
  },
];

const MODAL_WIDTH = 550;

const OptionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin: 10px 0 15px;
`;

// Plain buttons rather than role="radio": choosing a card advances at once,
// so there is never a persisted selection to announce.
const OptionCard = styled.button`
  all: unset;
  box-sizing: border-box;
  cursor: pointer;
  user-select: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 10px;
  padding: 24px 16px;
  border-radius: 6px;
  border: 2px solid ${({ theme }) => theme.palette.action.hover};
  background: ${({ theme }) =>
    theme.name === "DARK" ? "#2a2a2a" : theme.palette.background.default};
  color: ${({ theme }) => theme.palette.text.secondary};
  transition: border-color 120ms ease, color 120ms ease;

  &:hover {
    border-color: ${({ theme }) => theme.palette.primary.main};
    color: ${({ theme }) => theme.palette.primary.main};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.palette.primary.main};
    outline-offset: 3px;
  }
`;

const OptionLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.palette.text.primary};
`;

const OptionDescription = styled.div`
  font-size: 11px;
  line-height: 1.4;
  color: ${({ theme }) => theme.palette.text.secondary};
`;

interface CreateLabAssetModalProps {
  show: boolean;
  onClose: () => void;
  onSubmit: () => void;
  onCancel?: () => void;
}

export const CreateLabAssetModal: FC<CreateLabAssetModalProps> = ({
  show,
  onClose,
  onSubmit,
  onCancel,
}) => {
  const [step, setStep] = useState<"type" | "details">("type");
  const [assetType, setAssetType] = useState<LabAssetType | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // The first card, so focus lands on a card on open (via the Modal's
  // initialFocus) and on Back.
  const cardRef = useRef<HTMLButtonElement | null>(null);
  const detailsRef = useRef<HTMLDivElement | null>(null);

  // Each step change unmounts/hides the element that had focus, which would
  // drop focus to <body> inside the modal's focus trap. Re-home it.
  useEffect(() => {
    if (!show) return;
    if (step === "details") {
      detailsRef.current
        ?.querySelector<HTMLElement>("input, textarea")
        ?.focus();
    } else {
      cardRef.current?.focus();
    }
  }, [step, show]);

  const closeAndReset = useCallback(() => {
    setStep("type");
    setAssetType(null);
    onClose();
  }, [onClose]);

  // User-initiated close (the form's Cancel, the Modal's close button, or Esc
  // on the type step; the mfui Modal has no overlay/Esc dismiss of its own).
  // Guarded because closing mid-request would still create the row.
  const handleClose = useCallback(() => {
    if (submitting) return;
    closeAndReset();
  }, [submitting, closeAndReset]);

  // The type step has no Cancel, and the mfui close button only listens for
  // mousedown, so Esc is its keyboard exit. Not wired on the details step:
  // the SelectBox / DateInput popups there use Esc to close themselves, and
  // the form footer has Back and Cancel.
  useEffect(() => {
    if (!show || step !== "type") return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [show, step, handleClose]);

  // Success path. Deliberately unguarded: the form's submitting flag is still
  // true here (it clears in the form's finally), so routing through
  // handleClose would only work by reading a stale closure.
  const handleSubmit = () => {
    onSubmit?.(); // Refetches the assets table
    closeAndReset();
  };

  const handleCancel = () => {
    onCancel?.();
    handleClose();
  };

  // Choosing a card is the decision, so it advances on its own.
  const advanceTo = (type: LabAssetType) => {
    setAssetType(type);
    setStep("details");
  };

  const typeLabel =
    ASSET_TYPE_OPTIONS.find((option) => option.value === assetType)?.label ||
    "";

  return (
    // initialFocus: the mfui close button is the first tabbable element and
    // only listens for mousedown, and floating-ui applies initial focus after
    // our effects run, so this is the only way to land on a card on open.
    <Modal
      open={show}
      onClose={handleClose}
      showCloseButton={true}
      FocusProps={{ initialFocus: cardRef }}
    >
      <Modal.Title>Create Asset</Modal.Title>
      <Modal.SubTitle style={{ marginBottom: 15 }}>
        {step === "type"
          ? "Step 1 of 2 — What kind of asset are you adding?"
          : `Step 2 of 2 — New ${typeLabel} Asset`}
      </Modal.SubTitle>

      {step === "type" && (
        <div style={{ width: MODAL_WIDTH }}>
          {/* role="group": aria-label on a generic div is dropped by AT. */}
          <OptionGrid role="group" aria-label="Asset type">
            {ASSET_TYPE_OPTIONS.map((option, index) => (
              <OptionCard
                key={option.value}
                type="button"
                ref={index === 0 ? cardRef : undefined}
                onClick={() => advanceTo(option.value)}
              >
                {option.icon}
                <OptionLabel>{option.label}</OptionLabel>
                <OptionDescription>{option.description}</OptionDescription>
              </OptionCard>
            ))}
          </OptionGrid>
        </div>
      )}

      {/* Kept mounted behind step 1 once a type is chosen so entered field
          values survive Back; the Modal unmounts everything on close. Keyed
          by type so switching Hardware ↔ Software starts a fresh form rather
          than submitting one type's values under the other. */}
      {assetType && (
        <div
          ref={detailsRef}
          style={{ display: step === "details" ? "block" : "none" }}
        >
          <CreateLabAssetForm
            key={assetType}
            width={MODAL_WIDTH}
            assetType={assetType}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            onSubmittingChange={setSubmitting}
            onBack={() => setStep("type")}
          />
        </div>
      )}
    </Modal>
  );
};
